import {useDisclosure} from '@mantine/hooks';
import React, {useCallback, useImperativeHandle, useState} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {AntibodyEnforcedSchemaRole} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {getConflictingFieldNames} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {
  AntibodyComponentSchemaInterfaceId,
  ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE,
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import AntibodyComponentMigrationPreviewModal from './AntibodyComponentMigrationPreviewModal';

export interface AntibodyComponentMigrationControllerHandle {
  open: (spec: AntibodyEnforcedSchemaSpec, interfaceId: AntibodyComponentSchemaInterfaceId) => void;
}

export interface AntibodyComponentMigrationControllerProps {
  registryId: string;
  existingFields: Array<SchemaField | ClientSchemaField>;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  folderItemType: string | null;
  onMigrationRun: (antibodyRole: AntibodyEnforcedSchemaRole) => void | Promise<void>;
}

/**
 * Encapsulates the migration preview modal and its open/pending state so that
 * TagSchemaFileSettings (a large class component) can trigger the flow with a
 * single imperative `ref.current.open(spec, interfaceId)` call rather than
 * managing modal state itself.
 */
const AntibodyComponentMigrationController = React.forwardRef<
  AntibodyComponentMigrationControllerHandle,
  AntibodyComponentMigrationControllerProps
>(
  (
    {
      registryId,
      existingFields,
      existingConstraint,
      existingNamingStrategies,
      folderItemType,
      onMigrationRun,
    },
    ref
  ) => {
    const [opened, {open: openDisclosure, close}] = useDisclosure(false);
    const [pendingSpec, setPendingSpec] = useState<AntibodyEnforcedSchemaSpec | null>(null);
    const [pendingInterfaceId, setPendingInterfaceId] = useState<AntibodyComponentSchemaInterfaceId | null>(
      null
    );
    const [isApplying, setIsApplying] = useState(false);

    useImperativeHandle(ref, () => ({
      open(spec, interfaceId) {
        const {displayNameConflicts, systemNameConflicts} = getConflictingFieldNames(spec, existingFields);
        if (displayNameConflicts.length > 0) {
          notifications.error(
            `This schema has fields whose names conflict with built-in fields for this protein component type: ${displayNameConflicts.join(', ')}. Please rename these fields.`
          );
        }
        if (systemNameConflicts.length > 0) {
          notifications.error(
            `This schema has fields whose system names conflict with built-in fields for this protein component type: ${systemNameConflicts.join(', ')}. Please rename these fields.`
          );
        }
        if (displayNameConflicts.length > 0 || systemNameConflicts.length > 0) {
          return;
        }
        setPendingSpec(spec);
        setPendingInterfaceId(interfaceId);
        openDisclosure();
      },
    }));

    const handleApply = useCallback(async (): Promise<void> => {
      assert(pendingInterfaceId != null, 'Expected pendingInterfaceId when applying migration');

      const antibodyRole = ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[pendingInterfaceId];
      setIsApplying(true);
      try {
        await onMigrationRun(antibodyRole);
        close();
        // eslint-disable-next-line benchlint/always-throw-from-catch -- errors are surfaced via toast; swallowing here prevents an uncaught rejection while keeping the modal open for retry
      } catch (e) {
        notifications.notifyError(e);
      } finally {
        setIsApplying(false);
      }
    }, [close, onMigrationRun, pendingInterfaceId]);

    if (!opened || pendingSpec == null || pendingInterfaceId == null) {
      return null;
    }

    const componentTypeName = ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[pendingInterfaceId];
    return (
      <AntibodyComponentMigrationPreviewModal
        componentTypeName={componentTypeName}
        componentInterfaceId={pendingInterfaceId}
        enforcedSchemaSpec={pendingSpec}
        existingConstraint={existingConstraint}
        existingNamingStrategies={existingNamingStrategies}
        folderItemType={folderItemType}
        registryId={registryId}
        onClose={close}
        onApply={handleApply}
        isApplying={isApplying}
      />
    );
  }
);

export default AntibodyComponentMigrationController;
